import React, { useState, useEffect } from "react";
import { ArrowLeft, Share2, Send, AlertCircle, Clock, ChevronDown } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import PublicLayout from "./PublicLayout";
import { fetchShowcaseManuscript, fetchShowcaseTopSimilarPapers, fetchShowcaseDeepThinkers, fetchShowcaseAnalysis, downloadShowcaseExport } from "../service/ShowcaseService";
import { calculateOverallScore, sectionColor } from "../service/Operations";
import NetworkCanvas from "./NetworkCanvas";

function AnalysisGroups({ grouped, labelMap }) {
    const [open, setOpen] = useState({});
    const toggle = (key) => setOpen(s => ({ ...s, [key]: !s[key] }));
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {Object.entries(grouped).map(([scoreType, items]) => {
                const isOpen = !!open[scoreType];
                return (
                    <div key={scoreType} className="submissions-card">
                        <button className="analysis-group-header" onClick={() => toggle(scoreType)}>
                            <span>{labelMap[scoreType] ?? scoreType} Analysis</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span className="analysis-group-count">{items.length} {items.length === 1 ? "item" : "items"}</span>
                                <ChevronDown size={15} className={`analysis-chevron ${isOpen ? "analysis-chevron-open" : ""}`} />
                            </div>
                        </button>
                        {isOpen && (
                            <div className="analysis-items">
                                {items.map((item, i) => (
                                    <div key={item.id ?? i} className="analysis-item">
                                        {item.severity && (
                                            <span className={`analysis-severity severity-${item.severity.toLowerCase()}`}>
                                                {item.severity.charAt(0).toUpperCase() + item.severity.slice(1).toLowerCase()} Priority
                                            </span>
                                        )}
                                        <p className="analysis-issue">{item.issue}</p>
                                        {item.quote && <blockquote className="analysis-quote">{item.quote}</blockquote>}
                                        {item.suggestion && <p className="analysis-suggestion">{item.suggestion}</p>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default function ShowcaseSubmission() {
    const navigate = useNavigate();
    const { key } = useParams();
    const [paper, setPaper] = useState(null);
    const [similarPapers, setSimilarPapers] = useState([]);
    const [hasGraph, setHasGraph] = useState(false);
    const [hasDeepThinkers, setHasDeepThinkers] = useState(false);
    const [topDeepThinkers, setTopDeepThinkers] = useState([]);
    const [deepThinkersLoading, setDeepThinkersLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [similarLoading, setSimilarLoading] = useState(true);
    const [analyses, setAnalyses] = useState([]);
    const [exporting, setExporting] = useState(false);

    const [smallScreen, setSmallScreen] = useState(window.innerWidth < 991);

    useEffect(() => {
        const onResize = () => {
            const small = window.innerWidth < 991;
            setSmallScreen(small);
            if (small) setLoading(false);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        if (!key) { setLoading(false); setNotFound(true); return; }
        fetchShowcaseManuscript(key)
            .then(res => {
                if (res.manuscript) {
                    setPaper(res.manuscript);
                    setHasGraph(!!res.manuscript?.graphKey?.trim());
                    setHasDeepThinkers(!!res.manuscript?.deepThinkersKey?.trim());
                    if (res.manuscript?.status?.toLowerCase() === "completed") {
                        fetchShowcaseAnalysis(key)
                            .then(r => setAnalyses(r.analyses ?? []))
                            .catch(() => {});
                    }
                    if (res.hasSimilarPapers) {
                        fetchShowcaseTopSimilarPapers(key)
                            .then(r => setSimilarPapers(r.similarPapers ?? []))
                            .catch(() => {})
                            .finally(() => setSimilarLoading(false));
                    } else {
                        setSimilarLoading(false);
                    }
                    if (res.manuscript?.deepThinkersKey?.trim()) {
                        fetchShowcaseDeepThinkers(key)
                            .then(r => setTopDeepThinkers((r.deepThinkers ?? []).slice(0, 5)))
                            .catch(() => {})
                            .finally(() => setDeepThinkersLoading(false));
                    } else {
                        setDeepThinkersLoading(false);
                    }
                } else {
                    setNotFound(true);
                }
            })
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [key]);

    const handleExportDownload = async () => {
        setExporting(true);
        try {
            const { blob, disposition } = await downloadShowcaseExport(key);
            const filename = disposition?.split("filename=")[1]?.replace(/"/g, "").trim() || "report.pdf";
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = filename; a.click();
            URL.revokeObjectURL(url);
        } catch {}
        finally { setExporting(false); }
    };

    const scoreCards = paper ? [
        { label: "Novelty", value: paper.noveltyScore },
        { label: "Methodology", value: paper.methodScore },
        { label: "Impact", value: paper.impactScore },
        { label: "Overall", value: calculateOverallScore(paper) },
    ] : [];

    const status = paper?.status?.toLowerCase();
    const isCompleted = status === "completed";

    if (notFound && !loading) {
        return (
            <PublicLayout>
                <div className="upload-main" style={{ textAlign: "center", padding: "80px 20px" }}>
                    <AlertCircle size={40} color="var(--ink-200)" />
                    <p style={{ fontSize: 16, fontWeight: 600, marginTop: 16, color: "var(--ink-900)" }}>Paper not found</p>
                    <p style={{ fontSize: 13, color: "var(--ink-400)" }}>This showcase link may have been removed or is invalid.</p>
                </div>
            </PublicLayout>
        );
    }

    return (
        <PublicLayout>
            <div className="upload-main">
                <div className="upload-page-title-row">
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {loading ? (
                            <div className="skeleton" style={{ height: 22, width: "60%", marginBottom: 8 }} />
                        ) : (
                            <>
                                <div className="sample-badge">Showcase</div>
                                <h1 className="upload-page-heading">{paper?.title || paper?.name}</h1>
                                {paper?.authors && <p className="upload-page-meta">{paper.authors}</p>}
                                {paper?.uploadDate && (
                                    <p className="submission-upload-date">
                                        Uploaded {new Date(paper.uploadDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Processing state */}
                {!loading && !isCompleted && paper && (
                    <div className="sub-status-banner sub-status-processing">
                        <Clock size={18} />
                        <div>
                            <p className="sub-status-title">Analysis in progress</p>
                            <p className="sub-status-msg">This paper is being analyzed. Check back shortly for results.</p>
                        </div>
                    </div>
                )}

                {/* Score cards */}
                {(loading || isCompleted) && (
                    <div className="submission-scores">
                        {loading ? (
                            Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="submission-score-card">
                                    <div className="skeleton" style={{ height: 11, width: "55%", margin: "0 auto 10px" }} />
                                    <div className="skeleton" style={{ height: 32, width: "42%", margin: "0 auto 8px" }} />
                                </div>
                            ))
                        ) : scoreCards.map((s) => (
                            <div key={s.label} className="submission-score-card">
                                <p className="submission-score-label">{s.label}</p>
                                <p className="submission-score-value">{s.value}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* Actions */}
                {!loading && isCompleted && (
                    <div className="sub-actions-row">
                        <button className="sub-action-btn sub-action-outline" onClick={handleExportDownload} disabled={exporting}>
                            <Share2 size={13} />
                            {exporting ? "Downloading..." : "Export Report"}
                        </button>
                    </div>
                )}

                {!loading && isCompleted && (
                    <p className="beta-disclaimer">This product is in beta. Scores and analysis are experimental and may not fully reflect the quality of your work.</p>
                )}

                {/* Analysis Groups */}
                {!loading && isCompleted && (() => {
                    const analysisOrder = ["novelty", "methods", "impact"];
                    const analysisLabel = { novelty: "Novelty", methods: "Methodology", impact: "Impact" };
                    const analysisGrouped = analysisOrder.reduce((acc, type) => {
                        const items = analyses.filter(a => a.scoreType === type);
                        if (items.length > 0) acc[type] = items;
                        return acc;
                    }, {});
                    return Object.keys(analysisGrouped).length > 0 ? (
                        <AnalysisGroups grouped={analysisGrouped} labelMap={analysisLabel} />
                    ) : null;
                })()}

                {/* Research Network + DeepThinkers */}
                {!loading && isCompleted && <div className="submissions-card">
                    {hasGraph && !smallScreen && (
                        <>
                            <p className="similar-heading" style={{ padding: "16px 18px 0" }}>Research Network</p>
                            <div
                                className="submission-network-graph-full submission-network-clickable"
                                onClick={() => navigate(`/showcase/${key}/network`)}
                                title="View full network"
                            >
                                <div style={{ borderRadius: "12px", height: "25vh", position: "relative", background: "#0f1a14", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <NetworkCanvas />
                                    <div className="submission-network-view-hint">View Full Network →</div>
                                </div>
                            </div>
                        </>
                    )}

                    {hasDeepThinkers && (
                        <>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px 10px" }}>
                                <p className="similar-heading" style={{ padding: 0 }}>Top DeepThinkers</p>
                                <button
                                    onClick={() => navigate(`/showcase/${key}/deepthinkers`)}
                                    style={{ background: "none", border: "none", color: "var(--ink-400)", fontSize: 12, cursor: "pointer", padding: 0 }}
                                >
                                    View All →
                                </button>
                            </div>
                            <table className="submissions-table similar-works-table">
                                <thead>
                                    <tr>
                                        <th>Researcher</th>
                                        <th>Papers</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deepThinkersLoading ? (
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <tr key={i}>
                                                <td><div className="skeleton" style={{ height: 12, width: "65%", marginBottom: 7 }} /></td>
                                                <td><div className="skeleton" style={{ height: 12, width: 24, borderRadius: 6 }} /></td>
                                            </tr>
                                        ))
                                    ) : topDeepThinkers.length > 0 ? topDeepThinkers.map((dt, i) => (
                                        <tr key={i} className="submission-row" style={{ cursor: "pointer" }} onClick={() => navigate(`/showcase/${key}/deepthinkers`)}>
                                            <td><p style={{ margin: 0, fontWeight: 500, color: "var(--ink-900)" }}>{dt.name}</p></td>
                                            <td className="similar-works-pct">{dt.paperCount}</td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan={2}><p className="settings-empty">No DeepThinkers found.</p></td></tr>
                                    )}
                                </tbody>
                            </table>
                        </>
                    )}
                </div>}

                {/* Similar Papers */}
                {!loading && isCompleted && <div className="submissions-card">
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px 10px" }}>
                        <p className="similar-heading" style={{ padding: 0 }}>Top Similar Papers</p>
                        <button
                            onClick={() => navigate(`/showcase/${key}/similar`)}
                            style={{ background: "none", border: "none", color: "var(--ink-400)", fontSize: 12, cursor: "pointer", padding: 0 }}
                        >
                            View All →
                        </button>
                    </div>
                    <table className="submissions-table similar-works-table">
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th style={{ whiteSpace: "nowrap" }}>% Overlap</th>
                            </tr>
                        </thead>
                        <tbody>
                            {similarLoading ? (
                                Array.from({ length: 3 }).map((_, i) => (
                                    <tr key={i}>
                                        <td>
                                            <div className="skeleton" style={{ height: 12, width: "75%", marginBottom: 7 }} />
                                            <div className="skeleton" style={{ height: 10, width: "50%" }} />
                                        </td>
                                        <td><div className="skeleton" style={{ height: 12, width: 36, borderRadius: 6 }} /></td>
                                    </tr>
                                ))
                            ) : similarPapers.length > 0 ? similarPapers.map((p) => (
                                <tr key={p.id} className="submission-row" style={{ cursor: "pointer" }} onClick={() => navigate(`/showcase/${key}/similar`)}>
                                    <td>
                                        <p className="similar-works-title-link" style={{ margin: 0 }}>{p.articleTitle}</p>
                                        {p.section && (
                                            <div style={{ marginTop: 4 }}>
                                                <span className="similar-works-section-badge" style={sectionColor(p.section)}>{p.section}</span>
                                            </div>
                                        )}
                                        {p.authors && <p className="submission-authors" style={{ marginTop: 4 }}>{p.authors}</p>}
                                    </td>
                                    <td className="similar-works-pct">{Math.round(p.similarity * 100)}%</td>
                                </tr>
                            )) : (
                                <tr><td colSpan={2}><p className="settings-empty">No similar papers found.</p></td></tr>
                            )}
                        </tbody>
                    </table>
                </div>}
            </div>
        </PublicLayout>
    );
}
